import React from "react";
import { motion } from "framer-motion";
import { Target, Compass } from "lucide-react";

const InstituteOverview = () => {
    // Smooth, professional animation variants
    const fadeUpVariant = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white w-full border-t border-gray-100">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-center">
                    {/* LEFT: The Academic Narrative */}
                    <motion.div
                        className="lg:col-span-5 flex flex-col justify-center"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {/* Authoritative Headline */}
                        <motion.div variants={fadeUpVariant}>
                            <h2 className="text-4xl md:text-5xl font-black text-neutral-900 leading-[1.15] mb-6">
                                From{" "}
                                <span className="text-yellow-500">
                                    Aspirant To Officer.
                                </span>
                            </h2>
                            <div className="flex gap-2 mb-8">
                                <div className="w-12 h-1.5 bg-red-700 rounded-sm"></div>
                                <div className="w-4 h-1.5 bg-yellow-500 rounded-sm"></div>
                                <div className="w-1 h-1.5 bg-neutral-300 rounded-sm"></div>
                            </div>
                        </motion.div>

                        {/* Structured Text Content */}
                        <motion.div
                            variants={fadeUpVariant}
                            className="space-y-6 text-gray-600 text-lg leading-relaxed border-l-2 border-red-100 pl-6 font-medium"
                        >
                            <p>
                                At PYQ Pedia, we are dedicated to being your
                                one-stop destination for all Previous Year
                                Question (PYQ) needs.
                            </p>
                            <p className="text-base text-gray-500">
                                Our platform is specially designed to help
                                aspirants of UPSC, State PSC, SSC, TET, Railway,
                                DSSSB, and other competitive exams by providing
                                easy access to past question papers and their
                                detailed solutions.
                            </p>
                        </motion.div>
                    </motion.div>

                    {/* RIGHT: The Modern Layout Grid */}
                    <motion.div
                        className="lg:col-span-7"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {/* Main Media Module */}
                            <motion.div
                                variants={fadeUpVariant}
                                className="sm:col-span-2 relative rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm aspect-video group"
                            >
                                <img
                                    src="/assets/about-institute.jpg"
                                    alt="PYQ Pedia Institute"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                />
                                {/* Professional dark gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-transparent to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-500"></div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>

                {/* Vision & Mission Modules */}
                <motion.div
                    className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    {/* Vision Module (Dark Anchor) */}
                    <motion.div
                        variants={fadeUpVariant}
                        className="bg-neutral-900 rounded-xl p-8 border border-neutral-800 relative overflow-hidden group shadow-md"
                    >
                        {/* Structural Background Pattern */}
                        <div className="absolute top-0 right-0 w-full h-full opacity-[0.03] pointer-events-none bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>

                        <div className="w-12 h-12 bg-neutral-800 border border-neutral-700 rounded-lg flex items-center justify-center text-yellow-400 mb-6 relative z-10 group-hover:scale-105 transition-transform duration-300">
                            <Target size={24} />
                        </div>
                        <h3 className="text-xl font-black text-white mb-3 relative z-10">
                            Our Vision
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed relative z-10">
                            To become the most trusted and comprehensive
                            platform for competitive exam preparation by
                            empowering aspirants with easy access to previous
                            year questions, expert solutions, and innovative
                            learning resources.
                        </p>
                    </motion.div>

                    {/* Mission Module (Light Counterpart) */}
                    <motion.div
                        variants={fadeUpVariant}
                        className="bg-white rounded-xl p-8 border border-gray-200 relative overflow-hidden group shadow-sm hover:border-red-600 transition-colors duration-300"
                    >
                        {/* Edge Hover Accent */}
                        <div className="absolute top-0 left-0 w-1 h-0 bg-red-600 group-hover:h-full transition-all duration-500"></div>

                        <div className="w-12 h-12 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center text-red-700 mb-6 relative z-10 group-hover:scale-105 transition-transform duration-300">
                            <Compass size={24} />
                        </div>
                        <h3 className="text-xl font-black text-neutral-900 mb-3 relative z-10">
                            Our Mission
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed relative z-10 font-medium">
                            To empower aspirants with authentic PYQs, detailed
                            solutions, and smart exam insights. We simplify
                            preparation through structured programs and expert
                            guidance—making success achievable for every
                            aspirant.
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default InstituteOverview;
